import { useCallback, useEffect, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useTranslation } from "react-i18next";
import { Route } from "react-router-dom";
import { ColorScheme, ColorSchemeProvider, MantineProvider } from "@mantine/core";
import { useColorScheme, useHotkeys } from "@mantine/hooks";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { AppErrorBoundary } from "src/error/ErrorBoundary";
import { SentryRoutes } from "src/sentry";

import { Layout } from "../components/Layout";
import { resolveDatesLocale } from "../utils/helpers";
import { withAuth } from "../hooks/auth";
import { publicRoutes } from "../routes";
import { Error404Page } from "./404";
import { Header } from "./header";

const wrapperStyle = {
    minHeight: "100vh",
    display: "flex",
};

export const Pages = withAuth(() => {
    const { i18n } = useTranslation(["common"]);

    const preferredColorScheme = useColorScheme();
    const [colorScheme, setColorScheme] = useState<ColorScheme>(
        (localStorage.getItem("colorscheme") as ColorScheme) || preferredColorScheme
    );

    useEffect(() => {
        console.log(`%c${import.meta.env.VITE_RELEASE_VERSION}`, "color: #FF7804; font-size: 0.8rem");
    }, []);

    const toggleColorScheme = useCallback(
        (value?: ColorScheme) => {
            const newColorScheme = value || (colorScheme === "dark" ? "light" : "dark");
            setColorScheme(newColorScheme);
            localStorage.setItem("colorscheme", newColorScheme);
        },
        [colorScheme]
    );

    const defaultInputStyles = {
        input: {
            paddingInline: 16,
            height: "3.125rem",
        },
        innerInput: {
            paddingInline: 16,
            height: "3.125rem",
        },
    };

    useHotkeys([["mod+J", () => toggleColorScheme()]]);

    return (
        <ColorSchemeProvider colorScheme={colorScheme} toggleColorScheme={toggleColorScheme}>
            <MantineProvider
                theme={{
                    colorScheme,
                    datesLocale: resolveDatesLocale(i18n.language),
                    fontFamily: "Barlow, sans-serif",
                    primaryColor: "orange",
                    components: {
                        NumberInput: {
                            styles: defaultInputStyles,
                        },
                        TextInput: {
                            styles: defaultInputStyles,
                        },
                        PasswordInput: {
                            styles: defaultInputStyles,
                        },
                        Select: {
                            styles: defaultInputStyles,
                        },
                        MultiSelect: {
                            styles: {
                                input: {
                                    paddingLeft: 16,
                                },
                                values: {
                                    minHeight: "3.125rem",
                                },
                            },
                        },
                        DatePickerInput: {
                            styles: defaultInputStyles,
                        },
                    },
                    colors: {
                        primary: ["#FF7804", "#FF6200", "#ff790410"],
                        secondary: ["#30A9D4", "#0076E4"],
                        text: ["#0D1A33", "#888E98"],
                        border: ["#DADEE2"],
                    },
                }}
                withNormalizeCSS
                withGlobalStyles
                withCSSVariables
            >
                <DndProvider backend={HTML5Backend}>
                    <Notifications position="top-center" />
                    <ModalsProvider>
                        <Header />
                        <div style={wrapperStyle}>
                            <AppErrorBoundary>
                                <Layout>
                                    <SentryRoutes>
                                        {/* Public routes */}
                                        {publicRoutes.map((route) => (
                                            <Route key={route.path} path={route.path} element={route.element} />
                                        ))}

                                        {/* 404 route */}
                                        <Route path="*" element={<Error404Page />} />
                                    </SentryRoutes>
                                </Layout>
                            </AppErrorBoundary>
                        </div>
                    </ModalsProvider>
                </DndProvider>
            </MantineProvider>
        </ColorSchemeProvider>
    );
});
